import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProfileService } from '../../services/profile.service';
import { RoleService } from '../../services/role.service';
import {
  AppointmentsFilterModalComponent,
  FilterOptions,
} from '../appointments-filter-modal/appointments-filter-modal.component';
import { BaseToolbarComponent } from '../../campaign/shared/base-toolbar.component';

export interface ViewType {
  value: 'list' | 'calendar';
  label: string;
  icon: string;
}

export type { LocationOption } from '../../campaign/shared/base-toolbar.component';

/**
 * Toolbar used in appointment views to provide search, location selection, view switching (list/calendar) and filter controls.
 * This standalone component extends a base toolbar to load user locations, manage temporary selections, open a filter modal,
 * and emit events when the view or filter state changes. It also hides or alters location UI for staff users.
 * Key methods:
 *   - loadLocations(): loads available locations and initializes selection state from profile service.
 *   - onSearchClear(): clears the search input via base toolbar helper.
 *   - onViewSelect(view): switches between 'list' and 'calendar' views and emits viewChanged.
 *   - toggleLocationDropdown(): manages opening/closing the location dropdown and prepares temporary selection state.
 *   - onFilterClick(), onFilterModalClose(), onFiltersApplied(filters), onFiltersCleared(): manage filter modal visibility and emit corresponding events.
 *   - getSelectedLocationLabel(): returns a user-friendly label depending on dropdown state and selections.
 *   - getActiveFilterCount(): computes number of active filters from the activeFilters object.
 * Important lifecycle hooks:
 *   - ngOnInit(): calls loadLocations() to initialize location data and subscriptions.
 *   - ngOnDestroy(): inherited - ensure subscriptions are cleaned up by base class (override as needed).
 * @component
 * @selector app-appointments-toolbar
 * @example
 *   <app-appointments-toolbar
 *     [searchPlaceholder]="'Find appointments...'"
 *     [selectedView]="'calendar'"
 *     [selectedLocation]="'123'"
 *     [activeFilters]="currentFilters"
 *     [isAdminUser]="true"
 *     (viewChanged)="onViewChanged($event)"
 *     (filterClicked)="openFilterUi()"
 *     (filtersApplied)="applyFilters($event)"
 *     (filtersCleared)="clearFilters()">
 *   </app-appointments-toolbar>
 * @input {string} searchPlaceholder - Placeholder text for the search input (default: 'Search for appointments.').
 * @input {'list'|'calendar'} selectedView - Currently selected view type; either 'list' or 'calendar'.
 * @input {string} selectedLocation - Selected location id or label; used to initialize and display location selection.
 * @input {FilterOptions|null} activeFilters - Currently active filter options object (services, employees, locations, statuses).
 * @input {boolean} isAdminUser - Whether the current user has admin privileges (affects UI behavior).
 * @output {'list'|'calendar'} viewChanged - Emitted when the user switches between list and calendar views.
 * @output {void} filterClicked - Emitted when the filter button is clicked (modal should open).
 * @output {FilterOptions} filtersApplied - Emitted when filters are applied from the filter modal with the chosen options.
 * @output {void} filtersCleared - Emitted when filters are cleared from the filter modal.
 */
/** Shown when no location is picked, which now means the list spans every location. */
const ALL_LOCATIONS_LABEL = 'All Locations';

@Component({
  selector: 'app-appointments-toolbar',
  standalone: true,
  imports: [CommonModule, FormsModule, AppointmentsFilterModalComponent],
  templateUrl: './appointments-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './appointments-toolbar.component.scss',
})
export class AppointmentsToolbarComponent
  extends BaseToolbarComponent
  implements OnInit, OnDestroy
{
  @Input() override searchPlaceholder = 'Search for appointments.';
  @Input() selectedView: 'list' | 'calendar' = 'calendar';
  @Input() override selectedLocation = '';
  @Input() activeFilters: FilterOptions | null = null;
  @Input() isAdminUser = false;

  @Output() viewChanged = new EventEmitter<'list' | 'calendar'>();
  @Output() filterClicked = new EventEmitter<void>();
  @Output() filtersApplied = new EventEmitter<FilterOptions>();
  @Output() filtersCleared = new EventEmitter<void>();

  isFilterModalOpen = false;

  viewTypes: ViewType[] = [
    { value: 'list', label: 'List', icon: 'list-details' },
    { value: 'calendar', label: 'Calendar', icon: 'calendar' },
  ];

  constructor(
    profileService: ProfileService,
    private roleService: RoleService
  ) {
    super(profileService);
    this.restoreTempOnCancel = true;
  }

  override ngOnInit(): void {
    this.loadLocations();
  }

  /**
   * Loads locations from the profile service, transforms them into select options, initializes the selected location and temporary selection state, and emits a change for the initial selection.
   * @example
   * this.loadLocations()
   * void
   * @returns {void} No return value.
   */
  protected override loadLocations(): void {
    this.profileService.loadLocations();
    this.locationsSubscription = this.profileService.locations$.subscribe(apiLocations => {
      this.locations = apiLocations.map(location => ({
        value: location.id,
        label: location.branch,
        selected: location.id === this.selectedLocation,
      }));
      // No default selection: an empty selection means every location, which is what the list
      // shows on first load. Seeding locations[0] here silently scoped a multi-location shop to
      // its first branch, while the count cards above kept totalling them all.
      if (this.selectedLocation) {
        const resolved = this.locations.find(
          l => l.value === this.selectedLocation || l.label === this.selectedLocation
        );
        if (resolved) {
          this.tempSelectedLocations.add(resolved.value);
        } else {
          this.tempSelectedLocations.add(this.selectedLocation);
        }
      }
    });
  }

  onSearchClear(): void {
    this.clearSearch();
  }

  onViewSelect(view: 'list' | 'calendar'): void {
    this.selectedView = view;
    this.viewChanged.emit(view);
  }

  /**
   * Toggle the location dropdown open state and initialize temporary selections when opened.
   * @example
   * toggleLocationDropdown()
   * undefined
   * @returns {void} Toggles isLocationDropdownOpen and, when opening, populates tempSelectedLocations from selectedLocationLabels, selectedLocation, or the first available location.
   **/
  override toggleLocationDropdown(): void {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    if (this.isLocationDropdownOpen) {
      this.tempSelectedLocations.clear();
      if (this.selectedLocationLabels.length > 0) {
        this.selectedLocationLabels.forEach(label => {
          const location = this.locations.find(loc => loc.label === label);
          if (location) {
            this.tempSelectedLocations.add(location.value);
          }
        });
      } else if (this.selectedLocation) {
        this.tempSelectedLocations.add(this.selectedLocation);
      }
      // Nothing selected stays nothing selected: opening the dropdown must not quietly
      // narrow an all-locations list down to the first branch.
    }
  }

  onFilterClick(): void {
    this.isFilterModalOpen = true;
    this.filterClicked.emit();
  }

  onFilterModalClose(): void {
    this.isFilterModalOpen = false;
  }

  onFiltersApplied(filters: FilterOptions): void {
    this.filtersApplied.emit(filters);
    this.isFilterModalOpen = false;
  }

  onFiltersCleared(): void {
    this.filtersCleared.emit();
    // CWS-509: leave the popup open so the user can re-select without reopening it.
  }

  override onLocationSelectionFromModal(locationLabels: string[]): void {
    super.onLocationSelectionFromModal(locationLabels);
    this.locations.forEach(location => {
      location.selected = this.tempSelectedLocations.has(location.value);
    });
  }

  /**
   * Determine the display label for the location selector, taking into account whether the dropdown is open and any temporary or confirmed selections.
   * @example
   * getSelectedLocationLabel()
   * "Select Location"
   * @returns {string} The label to display for the location selector.
   **/
  override getSelectedLocationLabel(): string {
    // If dropdown is open, show temp selection
    if (this.isLocationDropdownOpen) {
      if (this.tempSelectedLocations.size === 0) {
        return ALL_LOCATIONS_LABEL;
      }
      if (this.tempSelectedLocations.size === 1) {
        const location = this.locations.find(
          loc => loc.value === Array.from(this.tempSelectedLocations)[0]
        );
        return location ? location.label : 'Select Location';
      }
      return `${this.tempSelectedLocations.size} locations`;
    }
    if (this.selectedLocationLabels.length === 0) {
      if (this.selectedLocation) {
        const location = this.locations.find(
          loc => loc.value === this.selectedLocation || loc.label === this.selectedLocation
        );
        return location ? location.label : ALL_LOCATIONS_LABEL;
      }
      return ALL_LOCATIONS_LABEL;
    }
    if (this.selectedLocationLabels.length === 1) {
      return this.selectedLocationLabels[0];
    }
    return `${this.selectedLocationLabels.length} locations`;
  }

  // Close dropdown when clicking outside
  override onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.location-dropdown')) {
      this.isLocationDropdownOpen = false;
    }
  }

  // Calculate the number of active filters
  /**
   * Returns the total number of active filters across services, employees, locations, and statuses.
   * @example
   * etActiveFilterCount()
   * 3
   * @returns {number} Total count of active filters.
   */
  getActiveFilterCount(): number {
    if (!this.activeFilters) {
      return 0;
    }

    let count = 0;
    count += this.activeFilters.services?.length || 0;
    count += this.activeFilters.employees?.length || 0;
    count += this.activeFilters.locations?.length || 0;
    count += this.activeFilters.statuses?.length || 0;

    return count;
  }

  // Check if current user is staff (hide location dropdown for staff)
  get isStaffUser(): boolean {
    return this.roleService.isShopFloorEmployee();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
